import { useState } from 'react';
import { C } from '../constants';
import { toDay, toTime, fmtDay } from '../utils';
import { Card, SLabel } from '../components/Atoms';
import { sendPush } from '../push';

// ── Section config ────────────────────────────────────────────────────────────
const SECTIONS = [
  { k: 'cardiology',  label: 'Cardiology',   color: '#FF6B9D', icon: '❤️', presets: ['ECG', '2D Echo'] },
  { k: 'radiology',   label: 'Radiology',    color: '#64D2FF', icon: '🩻', presets: ['X-Ray', 'CT', 'MRI', 'Ultrasound'] },
  { k: 'pathology',   label: 'Pathology',    color: '#FF9500', icon: '🧫', presets: ['Blood', 'Urine', 'Wound', 'Sputum', 'CSF'] },
  { k: 'nuclear',     label: 'Nuclear Med',  color: '#FFD60A', icon: '☢️', presets: ['Bone Scan', 'Thyroid Scan', 'PET Scan'] },
  { k: 'pulmonology', label: 'Pulmonology',  color: '#30D158', icon: '🫁', presets: ['PFT'] },
  { k: 'others',      label: 'Others',       color: '#8E8E93', icon: '📋', presets: [] },
];

const SEC_MAP = Object.fromEntries(SECTIONS.map(s => [s.k, s]));

// Pathology has extra fields (study type, result, organism, sensitivity)
const IS_PATHOLOGY = k => k === 'pathology';

const PATH_RESULTS = ['Pending', 'No Growth', 'Positive', 'Contaminated'];
const PATH_RES_COL = {
  'Pending':      { color: '#FF9500', bg: 'rgba(255,149,0,0.12)'   },
  'No Growth':    { color: '#30D158', bg: 'rgba(48,209,88,0.12)'   },
  'Positive':     { color: '#FF453A', bg: 'rgba(255,69,58,0.12)'   },
  'Contaminated': { color: '#8E8E93', bg: 'rgba(142,142,147,0.12)' },
};

// ── Blank form per section ────────────────────────────────────────────────────
const blankForm = () => ({
  preset:    '',
  custom:    '',
  study:     '',   // Pathology: Gram Stain, C&S etc.
  date:      toDay(),
  time:      toTime(),
  findings:  '',
  result:    'Pending',
  organism:  '',
  sensitiv:  '',
  notes:     '',
});

// ── Label helper ──────────────────────────────────────────────────────────────
const LBL = ({ children }) => (
  <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
    {children}
  </div>
);

export default function StudiesTab({ cur, patch, shiftCode }) {
  const [tab,   setTab]   = useState('cardiology');
  const [forms, setForms] = useState(() => Object.fromEntries(SECTIONS.map(s => [s.k, blankForm()])));
  const [showForm, setShowForm] = useState(false);

  const log = cur.studiesLog || [];

  // ── Form helpers ─────────────────────────────────────────────────────────────
  const f  = forms[tab];
  const setF = (field, val) => setForms(prev => ({ ...prev, [tab]: { ...prev[tab], [field]: val } }));

  const effectiveName = (frm) => frm.preset === '__custom__' ? frm.custom.trim() : frm.preset;

  // ── Save ─────────────────────────────────────────────────────────────────────
  const save = () => {
    const name = effectiveName(f);
    if (!name && !f.findings.trim() && !f.study.trim()) return;
    const sec = SEC_MAP[tab];
    const isPath = IS_PATHOLOGY(tab);
    const entry = {
      id:       `std${Date.now()}`,
      section:  tab,
      name:     name || sec.label,
      study:    f.study.trim(),
      date:     f.date,
      time:     f.time,
      findings: f.findings.trim(),
      // pathology-only fields — spread conditionally so Firestore never receives undefined
      ...(isPath ? {
        result:   f.result,
        organism: f.organism.trim(),
        sensitiv: f.sensitiv.trim(),
        notes:    f.notes.trim(),
      } : {}),
    };
    patch(p => ({ ...p, studiesLog: [...(p.studiesLog || []), entry] }));
    // notification
    if (shiftCode) {
      const pt = (cur.name || 'Patient') + (cur.room ? ' · Rm ' + cur.room : '');
      let body = pt + ' — ' + sec.label + ': ' + entry.name;
      if (IS_PATHOLOGY(tab)) body += ' → ' + f.result;
      sendPush(shiftCode, sec.icon + ' ' + sec.label, body, cur);
    }
    // reset form but keep preset selection
    setForms(prev => ({ ...prev, [tab]: { ...blankForm(), preset: prev[tab].preset } }));
    setShowForm(false);
  };

  // ── Update pathology result ───────────────────────────────────────────────────
  const updateResult = (id, newResult) => {
    const entry = log.find(e => e.id === id);
    if (!entry || entry.result === newResult) return;
    patch(p => ({ ...p, studiesLog: (p.studiesLog || []).map(e => e.id === id ? { ...e, result: newResult } : e) }));
    if (shiftCode) {
      const pt = (cur.name || 'Patient') + (cur.room ? ' · Rm ' + cur.room : '');
      sendPush(shiftCode, '🧫 Pathology Update', pt + ' — ' + entry.name + ': ' + newResult + (entry.organism ? ' (' + entry.organism + ')' : ''), cur);
    }
  };

  const del = id => { if (!window.confirm('Delete?')) return; patch(p => ({ ...p, studiesLog: (p.studiesLog || []).filter(e => e.id !== id) })); };

  // ── Log for current tab ───────────────────────────────────────────────────────
  const tabLog = [...log].filter(e => e.section === tab).sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));

  // ── Styles ────────────────────────────────────────────────────────────────────
  const IS = { width: '100%', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13 };
  const sec = SEC_MAP[tab];

  return (
    <>
      {/* 2×3 subtab grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 16 }}>
        {SECTIONS.map(s => {
          const active = tab === s.k;
          return (
            <button key={s.k} onClick={() => { setTab(s.k); setShowForm(false); }} style={{
              padding: '8px 4px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12,
              border: `1px solid ${active ? s.color : C.bd}`,
              background: active ? `${s.color}22` : 'transparent',
              color: active ? s.color : C.dim,
              transition: 'all 0.15s',
            }}>
              {s.label}
            </button>
          );
        })}
      </div>

      {!showForm && (
        <button onClick={() => { setShowForm(true); setForms(prev => ({ ...prev, [tab]: blankForm() })); }} style={{
          width: '100%', padding: '11px 0', borderRadius: 10, border: `1px dashed ${sec.color}66`,
          background: 'transparent', color: sec.color, fontSize: 13, fontWeight: 700,
          cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          ＋ New {sec.label} Entry
        </button>
      )}

      {showForm && (
      <Card s={{ border: `1px solid ${sec.color}44` }}>
        {/* Header + datetime */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <SLabel color={sec.color} mb={0}>New {sec.label} Entry</SLabel>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <input type="date" value={f.date} onChange={e => setF('date', e.target.value)}
              style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11 }} />
            <input type="time" value={f.time} onChange={e => setF('time', e.target.value)}
              style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11 }} />
          </div>
        </div>

        {/* Presets (only when section has them) */}
        {sec.presets.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <LBL>Type</LBL>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {sec.presets.map(p => {
                const active = f.preset === p && f.preset !== '__custom__';
                return (
                  <button key={p} onClick={() => setF('preset', p)} style={{
                    padding: '5px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                    border: `1px solid ${active ? sec.color : C.bd}`,
                    background: active ? `${sec.color}22` : 'transparent',
                    color: active ? sec.color : C.dim,
                    fontWeight: active ? 700 : 400,
                  }}>{p}</button>
                );
              })}
              {/* Custom option */}
              <button onClick={() => setF('preset', '__custom__')} style={{
                padding: '5px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                border: `1px solid ${f.preset === '__custom__' ? sec.color : C.bd}`,
                background: f.preset === '__custom__' ? `${sec.color}22` : 'transparent',
                color: f.preset === '__custom__' ? sec.color : C.dim,
                fontWeight: f.preset === '__custom__' ? 700 : 400,
              }}>Other…</button>
            </div>
          </div>
        )}

        {/* Custom name input — shown when Other… selected OR section has no presets */}
        {(f.preset === '__custom__' || sec.presets.length === 0) && (
          <div style={{ marginBottom: 10 }}>
            <LBL>{sec.presets.length === 0 ? 'Type / Name' : 'Specify'}</LBL>
            <input value={f.custom} onChange={e => setF('custom', e.target.value)}
              placeholder={`e.g. ${sec.k === 'cardiology' ? 'Holter Monitor' : sec.k === 'radiology' ? 'Fluoroscopy' : sec.k === 'pulmonology' ? 'Bronchoscopy' : sec.k === 'nuclear' ? 'MUGA Scan' : 'Specify study…'}`}
              style={IS} />
          </div>
        )}

        {/* Pathology: study type field */}
        {IS_PATHOLOGY(tab) && (
          <div style={{ marginBottom: 10 }}>
            <LBL>Study</LBL>
            <input value={f.study} onChange={e => setF('study', e.target.value)}
              placeholder="e.g. Gram Staining, Culture and Sensitivity, KOH prep"
              style={IS} />
          </div>
        )}

        {/* Pathology: result selector */}
        {IS_PATHOLOGY(tab) && (
          <div style={{ marginBottom: 10 }}>
            <LBL>Result</LBL>
            <div style={{ display: 'flex', gap: 6 }}>
              {PATH_RESULTS.map(r => {
                const rc = PATH_RES_COL[r];
                return (
                  <button key={r} onClick={() => setF('result', r)} style={{
                    flex: 1, padding: '6px 0', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                    border: `1px solid ${f.result === r ? rc.color : C.bd}`,
                    background: f.result === r ? rc.bg : 'transparent',
                    color: f.result === r ? rc.color : C.dim,
                  }}>{r}</button>
                );
              })}
            </div>
          </div>
        )}

        {/* Pathology: organism + sensitivity */}
        {IS_PATHOLOGY(tab) && (
          <>
            <div style={{ marginBottom: 8 }}>
              <LBL>Organism</LBL>
              <input value={f.organism} onChange={e => setF('organism', e.target.value)}
                placeholder="e.g. E. coli, S. aureus, No growth" style={IS} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <LBL>Sensitivity / Resistance</LBL>
              <textarea value={f.sensitiv} onChange={e => setF('sensitiv', e.target.value)}
                placeholder={'Sensitive to: Amoxicillin\nResistant to: Ampicillin'} rows={2}
                style={{ ...IS, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <LBL>Notes</LBL>
              <input value={f.notes} onChange={e => setF('notes', e.target.value)}
                placeholder="Additional notes…" style={IS} />
            </div>
          </>
        )}

        {/* Findings (non-pathology) */}
        {!IS_PATHOLOGY(tab) && (
          <div style={{ marginBottom: 8 }}>
            <LBL>Findings / Interpretation</LBL>
            <textarea value={f.findings} onChange={e => setF('findings', e.target.value)}
              placeholder="Findings and interpretation…" rows={3}
              style={{ ...IS, resize: 'vertical' }} />
          </div>
        )}

        <button onClick={save} style={{
          width: '100%', padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: '#1A3D6A', color: '#7DC4FF', fontSize: 13, fontWeight: 700,
        }}>
          💾 Save {sec.label} Entry
        </button>
        <button onClick={() => { setShowForm(false); setForms(prev => ({ ...prev, [tab]: blankForm() })); }}
          style={{ width: '100%', marginTop: 6, padding: '7px 0', borderRadius: 10, background: 'transparent', border: 'none', color: C.dim2, fontSize: 12, cursor: 'pointer' }}>
          ✕ Cancel
        </button>
      </Card>
      )}

      {/* ── Log for this section ── */}
      {tabLog.length === 0 && (
        <div style={{ textAlign: 'center', color: C.dim2, fontSize: 13, padding: '30px 0' }}>
          No {sec.label.toLowerCase()} entries recorded
        </div>
      )}

      {tabLog.map(e => {
        const rc = IS_PATHOLOGY(tab) ? (PATH_RES_COL[e.result] || PATH_RES_COL['Pending']) : null;
        return (
          <div key={e.id} style={{
            background: C.bg3, border: `1px solid ${C.bd}`,
            borderLeft: `3px solid ${rc ? rc.color : sec.color}`,
            borderRadius: 10, padding: '12px 14px', marginBottom: 8,
          }}>
            {/* Row 1: name + date + delete */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#E0EEFA' }}>
                  {e.name}{e.study ? <span style={{ fontSize: 11, fontWeight: 500, color: C.dim, marginLeft: 6 }}>— {e.study}</span> : ''}
                </div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{fmtDay(e.date)} · {e.time}</div>
              </div>
              <button onClick={() => del(e.id)} style={{ background: 'none', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
            </div>

            {/* Pathology: inline result buttons */}
            {IS_PATHOLOGY(tab) && (
              <div style={{ display: 'flex', gap: 5, marginBottom: 8 }}>
                {PATH_RESULTS.map(r => {
                  const rrc = PATH_RES_COL[r];
                  return (
                    <button key={r} onClick={() => updateResult(e.id, r)} style={{
                      flex: 1, padding: '4px 0', borderRadius: 6, cursor: 'pointer', fontSize: 10,
                      fontWeight: e.result === r ? 700 : 400,
                      border: `1px solid ${e.result === r ? rrc.color : C.bd}`,
                      background: e.result === r ? rrc.bg : 'transparent',
                      color: e.result === r ? rrc.color : C.dim2,
                    }}>{r}</button>
                  );
                })}
              </div>
            )}

            {e.organism  && <div style={{ marginBottom: 4 }}><div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>Organism</div><div style={{ fontSize: 12, color: rc?.color || sec.color, fontWeight: 700 }}>{e.organism}</div></div>}
            {e.sensitiv  && <div style={{ marginBottom: 4 }}><div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>Sensitivity / Resistance</div><div style={{ fontSize: 12, color: C.tx, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{e.sensitiv}</div></div>}
            {e.findings  && <div style={{ marginBottom: 4 }}><div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Findings / Interpretation</div><div style={{ fontSize: 12, color: C.tx, lineHeight: 1.6 }}>{e.findings}</div></div>}
            {e.notes     && <div><div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>Notes</div><div style={{ fontSize: 12, color: C.tx }}>{e.notes}</div></div>}
          </div>
        );
      })}
    </>
  );
}
